/** Meeting scheduling contract. Availability and bookings are decided by the owner (Cashier agenda). */
export type MeetingStatus = "SCHEDULED" | "CONFIRMED";
export type CustomerMeeting = Readonly<{ meetingId: string; date: string; time: string; durationMinutes: number; status: MeetingStatus; topic?: string; videoUrl?: string }>;
export type MeetingsOverview = Readonly<{ meetings: readonly CustomerMeeting[]; canBook: boolean; timeZone: string; minNoticeMinutes: number; horizonDays: number }>;
export type AvailabilitySlot = Readonly<{ time: string; durationMinutes: number }>;
export type AvailabilityDay = Readonly<{ date: string; slots: readonly AvailabilitySlot[] }>;
export type AvailabilityRule = Readonly<{ weekday: number; startTime: string; endTime: string; slotMinutes: number; isActive: boolean }>;
export type AvailabilityBlock = Readonly<{ date: string; reason?: string }>;
export type AvailabilityConfig = Readonly<{ rules: readonly AvailabilityRule[]; blocks: readonly AvailabilityBlock[] }>;
export type BookMeetingInput = Readonly<{ date: string; time: string; requestId: string; topic?: string }>;
export type CancelledMeeting = Readonly<{ meetingId: string; status: "CANCELLED" }>;

export const SLOT_LENGTHS = [15, 30, 45, 60, 90, 120] as const;

const record = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const DATE = /^\d{4}-\d{2}-\d{2}$/u;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/u;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const integer = (value: unknown, min: number, max: number): number | undefined => (typeof value === "number" && Number.isInteger(value) && value >= min && value <= max ? value : undefined);

export const meetingProjection = (input: unknown): CustomerMeeting | undefined => {
  if (!record(input)) return undefined;
  const duration = integer(input.durationMinutes, 5, 480);
  if (typeof input.meetingId !== "string" || !UUID.test(input.meetingId) || typeof input.date !== "string" || !DATE.test(input.date)
    || typeof input.time !== "string" || !TIME.test(input.time) || duration === undefined) return undefined;
  const topic = typeof input.topic === "string" && input.topic !== "" ? input.topic.slice(0, 500) : undefined;
  const videoUrl = typeof input.videoUrl === "string" && input.videoUrl.length <= 500 && /^https:\/\/[^\s]+$/u.test(input.videoUrl) ? input.videoUrl : undefined;
  return { meetingId: input.meetingId, date: input.date, time: input.time, durationMinutes: duration,
    status: input.status === "CONFIRMED" ? "CONFIRMED" : "SCHEDULED", ...(topic ? { topic } : {}), ...(videoUrl ? { videoUrl } : {}) };
};

export const meetingsOverviewProjection = (input: unknown): MeetingsOverview | undefined => {
  if (!record(input) || !Array.isArray(input.meetings) || input.meetings.length > 50 || typeof input.canBook !== "boolean") return undefined;
  const meetings = input.meetings.map(meetingProjection);
  const minNotice = integer(input.minNoticeMinutes, 0, 10080), horizon = integer(input.horizonDays, 1, 366);
  if (!meetings.every((item): item is CustomerMeeting => item !== undefined) || typeof input.timeZone !== "string" || minNotice === undefined || horizon === undefined) return undefined;
  return { meetings, canBook: input.canBook, timeZone: input.timeZone.slice(0, 64), minNoticeMinutes: minNotice, horizonDays: horizon };
};

export const availabilityProjection = (input: unknown): Readonly<{ days: readonly AvailabilityDay[] }> | undefined => {
  if (!record(input) || !Array.isArray(input.days) || input.days.length > 70) return undefined;
  const days: AvailabilityDay[] = [];
  for (const day of input.days) {
    if (!record(day) || typeof day.date !== "string" || !DATE.test(day.date) || !Array.isArray(day.slots) || day.slots.length > 96) return undefined;
    const slots: AvailabilitySlot[] = [];
    for (const slot of day.slots) {
      const duration = record(slot) ? integer(slot.durationMinutes, 5, 480) : undefined;
      if (!record(slot) || typeof slot.time !== "string" || !TIME.test(slot.time) || duration === undefined) return undefined;
      slots.push({ time: slot.time, durationMinutes: duration });
    }
    days.push({ date: day.date, slots });
  }
  return { days };
};

export const availabilityConfigProjection = (input: unknown): AvailabilityConfig | undefined => {
  if (!record(input) || !Array.isArray(input.rules) || input.rules.length > 28 || !Array.isArray(input.blocks) || input.blocks.length > 200) return undefined;
  const rules: AvailabilityRule[] = [];
  for (const rule of input.rules) {
    const weekday = record(rule) ? integer(rule.weekday, 0, 6) : undefined;
    const slotMinutes = record(rule) ? integer(rule.slotMinutes, 5, 480) : undefined;
    if (!record(rule) || weekday === undefined || slotMinutes === undefined || typeof rule.startTime !== "string" || !TIME.test(rule.startTime)
      || typeof rule.endTime !== "string" || !TIME.test(rule.endTime) || typeof rule.isActive !== "boolean") return undefined;
    rules.push({ weekday, startTime: rule.startTime, endTime: rule.endTime, slotMinutes, isActive: rule.isActive });
  }
  const blocks: AvailabilityBlock[] = [];
  for (const block of input.blocks) {
    if (!record(block) || typeof block.date !== "string" || !DATE.test(block.date)) return undefined;
    const reason = typeof block.reason === "string" && block.reason !== "" ? block.reason.slice(0, 160) : undefined;
    blocks.push({ date: block.date, ...(reason ? { reason } : {}) });
  }
  return { rules, blocks };
};

export const cancelledMeetingProjection = (input: unknown): CancelledMeeting | undefined =>
  record(input) && typeof input.meetingId === "string" && UUID.test(input.meetingId) && input.status === "CANCELLED" ? { meetingId: input.meetingId, status: "CANCELLED" } : undefined;

/** One entry point for the BFF: picks the strict projection for the meeting resources. */
export const meetingsResponseProjection = (resource: "meetings" | "meeting-availability" | "meeting-rules", mutation: boolean, action: string | undefined, body: unknown): unknown => {
  if (resource === "meeting-availability") return availabilityProjection(body);
  if (resource === "meeting-rules") return availabilityConfigProjection(body);
  if (!mutation) return meetingsOverviewProjection(body);
  return action === "cancel" ? cancelledMeetingProjection(body) : meetingProjection(body);
};
